import React, { useState, useEffect, useContext, useRef } from "react";
import { API_URL } from "../components/api";
import "bootstrap/dist/css/bootstrap.css";
import Axios from "axios";
import AuthContext from "../context/AuthContext";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

/** Reusable confirmation dialog */
function ConfirmDialog({
  show,
  title = "Confirm",
  body,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmVariant = "primary",
  onConfirm,
  onCancel,
  disableActions = false,
}) {
  return (
    <Modal
      show={show}
      onHide={disableActions ? undefined : onCancel}
      centered
      backdrop={disableActions ? "static" : true}
      keyboard={!disableActions}
    >
      <Modal.Header closeButton={!disableActions}>
        <Modal.Title className="fw-semibold">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{body}</Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={disableActions}
        >
          {cancelText}
        </Button>
        <Button
          variant={confirmVariant}
          onClick={onConfirm}
          disabled={disableActions}
        >
          {confirmText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function IPTVAddons(props) {
  const { user } = useContext(AuthContext);
  const authTokens = JSON.parse(localStorage.getItem("authTokens"));

  // Data state
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selector state
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectorLoading, setSelectorLoading] = useState(true);
  const [selectedAddonId, setSelectedAddonId] = useState("");

  // Action states
  const [adding, setAdding] = useState(false);
  const [deletingIds, setDeletingIds] = useState(() => new Set());

  // Confirmation modal states
  const [showAddConfirm, setShowAddConfirm] = useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = useState(null);

  const isMountedRef = useRef(true);

  const authHeaders = {
    Authorization: `Bearer ${authTokens?.access}`,
    "Content-Type": "application/json",
  };

  // Normalize possible link-id field names (ID of the *link*, not the add-on itself)
  const getAddonLinkId = (row) =>
    row?.iptv_subscription_addon_id ??
    row?.iptv_account_addon_id ??
    row?.subscription_addon_id ??
    row?.iptv_addon_subscription_id ??
    null;

  // ---------- Fetchers ----------
  const fetchAddons = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        `${API_URL}/radius/subscriptions/iptv/addons/`,
        {
          headers: authHeaders,
          params: {
            username: user?.username,
            iptv_id: props.iptv_id,
          },
        }
      );

      if (response.status === 200 && isMountedRef.current) {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data
          ? [response.data]
          : [];
        setAddons(data);
      }
    } catch (err) {
      console.error("Error fetching addons:", err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const fetchAvailable = async () => {
    try {
      const res = await Axios.get(`${API_URL}/radius/plans/iptv/addons`, {
        headers: authHeaders,
        params: { username: user?.username },
      });
      if (res.status === 200) {
        const data = Array.isArray(res.data)
          ? res.data
          : res.data
          ? [res.data]
          : [];
        setAvailableAddons(data);
      }
    } catch (e) {
      console.error("Error fetching available add-ons:", e);
    } finally {
      setSelectorLoading(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchAddons();
    fetchAvailable();
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokens?.access, user?.username, props.iptv_id]);

  // ---------- Add flow ----------
  const openAddConfirm = () => {
    if (!selectedAddonId) return;
    setShowAddConfirm(true);
  };

  const performAdd = async () => {
    if (!selectedAddonId) return;
    setAdding(true);
    try {
      await Axios.post(
        `${API_URL}/radius/subscriptions/iptv/addons/`,
        {
          Action: "Post",
          iptv_id: props.iptv_id, // IPTVSubscription object in backend
          iptv_addon_id: selectedAddonId, // IPTVAddon object in backend
          userdealer: user?.username,
        },
        { headers: authHeaders }
      );
      setSelectedAddonId("");
      await fetchAddons();
    } catch (err) {
      console.error("Failed to add IPTV add-on:", err);
    } finally {
      setAdding(false);
      setShowAddConfirm(false);
    }
  };

  // ---------- Delete flow ----------
  const openDeleteConfirm = (row) => {
    const linkId = getAddonLinkId(row);
    if (!linkId) {
      // No link id means we cannot unlink this row
      return;
    }
    setPendingDeleteRow(row);
  };

  const performDelete = async () => {
    const row = pendingDeleteRow;
    if (!row) return;
    const linkId = getAddonLinkId(row);
    if (!linkId) return;

    setDeletingIds((prev) => {
      const next = new Set(prev);
      next.add(linkId);
      return next;
    });

    try {
      await Axios.post(
        `${API_URL}/radius/subscriptions/iptv/addons/`,
        {
          Action: "Put", // unlink per your backend
          iptv_subscription_addon_id: linkId,
          userdealer: user?.username,
        },
        { headers: authHeaders }
      );
      await fetchAddons();
    } catch (err) {
      console.error("Failed to delete IPTV add-on:", err);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(linkId);
        return next;
      });
      setPendingDeleteRow(null);
    }
  };

  // ---------- Helpers ----------
  const selectedObj =
    availableAddons.find(
      (a) => String(a.iptv_addon_id) === String(selectedAddonId)
    ) || {};

  return (
    <div className="testii">
      <div className="mod">
        {/* Selector + Add button */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <select
            className="form-select"
            value={selectedAddonId}
            onChange={(e) => setSelectedAddonId(e.target.value)}
            disabled={selectorLoading || availableAddons.length === 0 || adding}
            style={{ maxWidth: 360 }}
            aria-label="Select IPTV add-on"
          >
            <option value="">
              {selectorLoading ? "Loading add-ons..." : "Select an add-on"}
            </option>
            {availableAddons.map((opt) => (
              <option key={opt.iptv_addon_id} value={opt.iptv_addon_id}>
                {opt.addon_name} (ID: {opt.iptv_addon_id})
              </option>
            ))}
          </select>

          <Button
            variant="primary"
            onClick={openAddConfirm}
            disabled={!selectedAddonId || adding}
            title="Link selected add-on to this IPTV subscription"
          >
            {adding ? "Adding…" : "Add"}
          </Button>
        </div>

        {/* Table */}
        <table className="table">
          <thead className="table-dark">
            <tr>
              <th>Add-on Name</th>
              <th>Add-on ID</th>
              <th style={{ width: 140 }} className="text-end">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3}>Loading…</td>
              </tr>
            ) : addons.length === 0 ? (
              <tr>
                <td colSpan={3}>No add-ons</td>
              </tr>
            ) : (
              addons.map((item, idx) => {
                const linkId = getAddonLinkId(item);
                const isDeleting = linkId != null && deletingIds.has(linkId);
                return (
                  <tr key={linkId ?? item.iptv_addon_id ?? idx}>
                    <td>{item.addon_name ?? "-"}</td>
                    <td>{item.iptv_addon_id ?? "-"}</td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => openDeleteConfirm(item)}
                        disabled={!linkId || isDeleting}
                        title={
                          linkId
                            ? "Remove this add-on from the IPTV subscription"
                            : "Cannot delete: missing link id"
                        }
                      >
                        {isDeleting ? "Deleting…" : "Delete"}
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add confirmation modal */}
      <ConfirmDialog
        show={showAddConfirm}
        title="Link add-on"
        body={
          <div>
            Link{" "}
            <strong>
              {selectedObj.addon_name ?? "Add-on"} (ID:{" "}
              {selectedObj.iptv_addon_id ?? selectedAddonId})
            </strong>{" "}
            to IPTV subscription <strong>ID {props.iptv_id}</strong>?
          </div>
        }
        confirmText={adding ? "Linking…" : "Link"}
        confirmVariant="primary"
        disableActions={adding}
        onConfirm={performAdd}
        onCancel={() => !adding && setShowAddConfirm(false)}
      />

      {/* Delete confirmation modal */}
      <ConfirmDialog
        show={!!pendingDeleteRow}
        title="Remove add-on"
        body={
          pendingDeleteRow ? (
            <div>
              Remove{" "}
              <strong>
                {pendingDeleteRow.addon_name ?? "Add-on"} (ID:{" "}
                {pendingDeleteRow.iptv_addon_id ?? "?"})
              </strong>{" "}
              from this IPTV subscription?
            </div>
          ) : null
        }
        confirmText={
          pendingDeleteRow &&
          deletingIds.has(getAddonLinkId(pendingDeleteRow))
            ? "Removing…"
            : "Remove"
        }
        confirmVariant="danger"
        disableActions={
          pendingDeleteRow
            ? deletingIds.has(getAddonLinkId(pendingDeleteRow))
            : false
        }
        onConfirm={performDelete}
        onCancel={() => {
          const row = pendingDeleteRow;
          if (!row) return;
          const busy =
            row && deletingIds.has(getAddonLinkId(row) ?? "__none__");
          if (!busy) setPendingDeleteRow(null);
        }}
      />
    </div>
  );
}

export default IPTVAddons;
